import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";
import { SHOWROOM } from "@/constants/testIds";

const CTA_META = {
  interest: { title: "Express Interest", blurb: "Tell us you love this one — we'll be in touch with the good stuff." },
  info: { title: "Request More Information", blurb: "Get full specs, pricing and delivery options for this model." },
  callback: { title: "Request a Call Back", blurb: "Leave your number and our team will call you back shortly." },
};

export const LeadForm = ({ ctaType, spa, room, onClose }) => {
  const meta = CTA_META[ctaType] || CTA_META.interest;
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Please enter your name");
    if (ctaType === "callback" && !form.phone.trim()) return toast.error("Please enter your phone number");
    if (ctaType !== "callback" && !form.email.trim()) return toast.error("Please enter your email");
    setLoading(true);
    try {
      await api.post("/leads", {
        cta_type: ctaType,
        spa_id: spa?.id || null,
        spa_name: spa?.name || "",
        room: room || "",
        name: form.name,
        email: form.email || null,
        phone: form.phone,
        message: form.message,
      });
      setDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[60] flex items-center justify-center p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
        <motion.div
          className="relative w-full max-w-md overflow-hidden rounded-2xl border border-white/10 bg-[#1d1f26]/95 p-6 backdrop-blur-xl"
          initial={{ y: 24, opacity: 0, scale: 0.98 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: 24, opacity: 0 }}
          transition={{ type: "spring", stiffness: 260, damping: 24 }}
          data-testid={SHOWROOM.leadForm}
        >
          <button
            onClick={onClose}
            className="absolute right-4 top-4 text-slate-400 transition-colors hover:text-white"
            data-testid="lead-form-close"
          >
            <X size={20} />
          </button>

          {done ? (
            <div className="py-8 text-center" data-testid={SHOWROOM.leadSuccess}>
              <CheckCircle2 className="mx-auto mb-4 text-cyan-400" size={48} />
              <h3 className="font-display text-2xl font-semibold tracking-tight">You're all set</h3>
              <p className="mt-2 text-sm text-slate-400">
                Thanks {form.name.split(" ")[0]} — your enquiry is on its way to the Just Spas team.
                {ctaType === "callback" ? " We'll call you back shortly." : " We'll be in touch soon."}
              </p>
              <Button
                onClick={onClose}
                className="mt-6 rounded-full bg-cyan-500 px-6 text-black hover:bg-cyan-400"
              >
                Back to showroom
              </Button>
            </div>
          ) : (
            <>
              <span className="text-xs uppercase tracking-widest text-cyan-400">
                {spa?.name || "Just Spas Newcastle"}
              </span>
              <h3 className="mt-1 font-display text-2xl font-semibold tracking-tight">{meta.title}</h3>
              <p className="mt-1 text-sm text-slate-400">{meta.blurb}</p>

              <form onSubmit={submit} className="mt-5 space-y-3">
                <Input
                  placeholder="Your name"
                  value={form.name}
                  onChange={set("name")}
                  className="border-white/10 bg-white/5 focus-visible:ring-cyan-500"
                  data-testid={SHOWROOM.leadName}
                />
                <Input
                  type="email"
                  placeholder={ctaType === "callback" ? "Email (optional)" : "Email address"}
                  value={form.email}
                  onChange={set("email")}
                  className="border-white/10 bg-white/5 focus-visible:ring-cyan-500"
                  data-testid={SHOWROOM.leadEmail}
                />
                <Input
                  placeholder={ctaType === "callback" ? "Phone number" : "Phone (optional)"}
                  value={form.phone}
                  onChange={set("phone")}
                  className="border-white/10 bg-white/5 focus-visible:ring-cyan-500"
                  data-testid={SHOWROOM.leadPhone}
                />
                <Textarea
                  placeholder="Anything you'd like us to know? (optional)"
                  value={form.message}
                  onChange={set("message")}
                  className="min-h-[80px] border-white/10 bg-white/5 focus-visible:ring-cyan-500"
                  data-testid={SHOWROOM.leadMessage}
                />
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-full bg-cyan-500 py-6 text-base font-semibold text-black transition-colors hover:bg-cyan-400"
                  data-testid={SHOWROOM.leadSubmit}
                >
                  {loading ? <Loader2 className="animate-spin" size={18} /> : "Send my enquiry"}
                </Button>
                <p className="pt-1 text-center text-xs text-slate-500">
                  Prefer to talk? Call us on 02 4942 3212
                </p>
              </form>
            </>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
